import React, { useState } from 'react'
import {
  Modal,
  useModalContext,
  Button,
  get,
  useTriggerSnackbar,
} from '@ally/metronome-ui'
import styled from 'styled-components'
import { Forms } from '@ally/forms'
import dayjs from 'dayjs'
import allytm from '@ally/allytm'
import EditIcon from '../../../assets/icons/edit_square.svg'
import {
  Beneficiary,
  BeneficiaryIdentifier,
  EditPayload,
  EditPayloadObject,
  TaxIdentifier,
} from '../../../types'
import {
  GENERIC_ERROR_MESSAGE,
  IDENTIFIER_NAMES,
} from '../../../utils/constants'
import {
  convertDateToSafeFormat,
  maskSSN,
  maskTaxId,
  removeLeadingZeros,
  formatDOBforInput,
} from '../../../utils/formatter'
import { useBeneficiariesApi } from '../../../api/beneficiaries-api'
import { EditBeneficiaryForm } from './EditBeneficiaryForm'
import { useNonIraBeneficiaries } from '../../../services/NonIraBeneficiariesProvider'
import { baseValidationSchema } from '../../../utils/yupValidationSchema'
import { EditBeneficiaryVerifyPage } from './EditBeneficiaryVerifyPage'
import { useFeatureFlags } from '../../../hooks/use-feature-flags'
import { Flags } from '../../../utils/feature-flags'

const SSN = 'SocialSecurityNumber'
const TAX_ID = 'TaxId'

export type Percentages = Record<string, number>
export interface EditFormValues {
  dateOfBirth: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  zipCode: string
  beneIdentification: string
  maskedSsn: string
  maskedTaxId: string
  identificationNumber: string
  issuer: string
  issueDate: string
  expirationDate: string
  fundAllocationType: 'equal' | 'specific'
}

const StyledEditButton = styled(Button)`
  ${get.media.down('md')} {
    margin-right: ${get.space('md')};
  }
`

const EditButton: React.FC<{
  isIraPage: boolean
  beneficiaryId: string
  accountId: string
  accountLabel: string
}> = ({ isIraPage, beneficiaryId, accountId, accountLabel }) => {
  const { setOpen } = useModalContext()

  return (
    <StyledEditButton
      onClick={(): void => {
        // istanbul ignore next - tagging
        allytm.event(
          'modalView',
          isIraPage
            ? 'AOS:Beneficiaries:Bank IRAs:Edit Beneficiary'
            : 'AOS:Beneficiaries:Bank Checking Savings and CDs:Edit Beneficiary',
          {
            treatAsPageview: true,
          },
        )
        setOpen(true)
      }}
      unsupportedContent={<EditIcon />}
      variant="link"
      data-testid={`${beneficiaryId}-edit-modal-trigger`}
      id={`${beneficiaryId}-edit-modal-trigger`}
      ariaLabel="Edit Beneficiary"
      allytmln={`edit beneficiary-${accountLabel}`}
    />
  )
}

export const EditModalContent: React.FC<{
  beneficiaryId: string
  beneficiaries: Beneficiary[] | undefined
  accountId: string
  isIraPage?: boolean
}> = ({ beneficiaryId, beneficiaries, accountId, isIraPage }) => {
  const { updateBeneficiary } = useBeneficiariesApi()
  const [showErrorMessage, setShowErrorMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const { getBeneficiaries } = useNonIraBeneficiaries()
  const { triggerSnackbar } = useTriggerSnackbar()
  const [isVerifyPage, setIsVerifyPage] = useState(false)

  const ffSsnTaxIdUiMaskingEnabled = useFeatureFlags(
    Flags.SsnTaxIdUiMaskingEnabled,
  )

  // istanbul ignore next // chaining option
  const currentBeneficiary = beneficiaries?.find(
    bene => bene.beneficiaryId === beneficiaryId,
  )

  // istanbul ignore next // low value
  if (!currentBeneficiary || !beneficiaries) return <></>

  const {
    dateOfBirth,
    address,
    allocationPercentage,
    taxIdentifier,
    taxId,
    beneficiaryClassificationCode,
  } = currentBeneficiary
  const { addressLine1, city, zipCode, state, addressLine2 } = address

  // if there was no tax identifier for current bene we have to pass an array only with new identification details
  // otherwise we have to pass an array with different objects including what we had and the new identification details
  const updatedTaxIdentifier = (values: EditFormValues): TaxIdentifier[] => {
    const identificationSelection =
      values.beneIdentification &&
      Object.keys(IDENTIFIER_NAMES).find(
        key =>
          IDENTIFIER_NAMES[key as BeneficiaryIdentifier] ===
          values.beneIdentification,
      )
    // Not passing current beneficiary tax identifier data.
    // Because backend system ignoring existing beneficiary identifier data and overriding with latest values.
    return [
      {
        type:
          identificationSelection || // istanbul ignore next // low value
          '',
        typeValue: values.identificationNumber,
        issuer: values.issuer,
        issuedDate: dayjs(values.issueDate).format('MM-DD-YYYY').toString(),
        expiryDate: dayjs(values.expirationDate)
          .format('MM-DD-YYYY')
          .toString(),
      },
    ]
  }

  // istanbul ignore next - low value, modifies tax id for payload if tax id came masked from API
  const taxIdValueForPayload = (value?: string): string | undefined => {
    if (
      !ffSsnTaxIdUiMaskingEnabled &&
      value?.toLowerCase().indexOf('x') !== -1
    ) {
      return undefined
    }
    return value
  }

  const equalAllocationPercentage = beneficiaries.length <= 1 ? '100' : 'Equal'
  // we need to normalize data for updated current bene before we pass it in the payload
  const currentBeneficiaryForPayload = (
    values: EditFormValues,
  ): EditPayloadObject => {
    const commonPayload = {
      beneficiaryId,
      address: {
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2,
        city: values.city,
        country: 'US',
        state: values.state,
        zipCode: values.zipCode,
      },
      allocationPercentage:
        values.fundAllocationType === 'equal'
          ? equalAllocationPercentage
          : removeLeadingZeros(values[beneficiaryId as keyof EditFormValues]) || // istanbul ignore next // low value
            '0',
      taxId:
        values.beneIdentification === SSN ||
        values.beneIdentification === TAX_ID
          ? taxIdValueForPayload(
              currentBeneficiary.taxId || values.identificationNumber,
            )
          : undefined,
      taxIdentifier:
        values.beneIdentification === SSN
          ? currentBeneficiary.taxIdentifier
          : updatedTaxIdentifier(values),
    }
    if (beneficiaryClassificationCode === 'I') {
      return {
        ...commonPayload,
        dateOfBirth: dayjs(values.dateOfBirth).format('YYYY-MM-DD').toString(),
      }
    }
    return commonPayload
  }

  const createPayloadObject = (values: EditFormValues): EditPayload => {
    // there are two different scenarios, one where beneficiaries have specific allocation percentages
    // in which we have to pass all beneficiaries with bene id and allocation percentage.
    // the other scenario would be equal allocation for which we only need to pass the current updated bene.
    const updatedCurrentBene = currentBeneficiaryForPayload(values)
    if (values.fundAllocationType === 'equal') {
      return {
        beneficiaries: [updatedCurrentBene],
      }
    }
    // we need to filter the updated beneficiary since we'll be passing new values for that one and we need to create
    // customized objects for each bene with allocation percentage and id.
    const allBenePayload = beneficiaries
      .filter(bene => bene.beneficiaryId !== beneficiaryId)
      .map(bene => ({
        beneficiaryId: bene.beneficiaryId,
        allocationPercentage: removeLeadingZeros(
          values[bene.beneficiaryId as keyof EditFormValues],
        ),
      }))
    return {
      beneficiaries: [updatedCurrentBene, ...allBenePayload],
    }
  }

  const handleSubmit = async (values: EditFormValues): Promise<void> => {
    const payload = createPayloadObject(values)

    try {
      setShowErrorMessage('')
      setIsLoading(true)
      const response = await updateBeneficiary(accountId, payload)
      if (response.error && response.error.errorCode === 'USB_BUS_635') {
        setShowErrorMessage(GENERIC_ERROR_MESSAGE)
        allytm.event(
          'customError',
          `We couldn't process your request. Please contact Customer Service at 1-877-247-2559.`,
          'USB_BUS_635',
        )
        return
      }
      // istanbul ignore next // we have a test for this already
      await getBeneficiaries()
      // istanbul ignore next
      triggerSnackbar({
        text: 'You updated your beneficiaries.',
        columnSize: 4,
        snackbarButtonText: 'Close',
        onClose: () =>
          requestAnimationFrame(() => {
            document
              .getElementById(`${beneficiaryId}-edit-modal-trigger`)
              ?.focus()
          }),
      })
      // istanbul ignore next - tagging
      allytm.event('customEvent', {
        name: 'formSuccess',
        type: 'formValidationSuccess',
        attr: {
          formValues: {
            form: 'Edit Non-IRA Beneficiary',
            values: [],
          },
        },
      })
    } catch (e) {
      setShowErrorMessage(GENERIC_ERROR_MESSAGE)
      allytm.event(
        'customError',
        `We couldn't process your request. Please contact Customer Service at 1-877-247-2559.`,
        '',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const stepBack = (message: string): void => {
    setIsVerifyPage(false)
    setShowErrorMessage(message)
  }

  const stepForward = (): void => {
    setShowErrorMessage('')
    setIsVerifyPage(true)
  }

  const initialValues = beneficiaries.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.beneficiaryId]:
        curr.allocationPercentage === 'Equal'
          ? '0'
          : Math.round(Number(curr.allocationPercentage)),
    }),
    {},
  )

  const currentTaxIdentifier =
    taxIdentifier &&
    taxIdentifier.length &&
    IDENTIFIER_NAMES[taxIdentifier[0].type as BeneficiaryIdentifier]

  const currentTaxIdentifierValue =
    taxIdentifier &&
    taxIdentifier.length &&
    (taxIdentifier[0].typeValue as BeneficiaryIdentifier)

  const currentIdentification = taxId ? SSN : currentTaxIdentifier

  // istanbul ignore next // low value
  const currentSsn = taxId ? maskSSN(taxId) : ''
  const currentTaxId = taxId ? maskTaxId(taxId) : ''
  const currentSsnOrTaxId =
    taxId && taxId.indexOf('-') === 3 ? currentSsn : currentTaxId

  const currentIdentificationNumber = taxId
    ? currentSsnOrTaxId
    : currentTaxIdentifierValue

  return (
    <Forms
      formId="edit-beneficiaries"
      validationSchema={baseValidationSchema}
      initialValues={{
        ...initialValues,
        dateOfBirth: formatDOBforInput(dateOfBirth),
        addressLine1,
        addressLine2: addressLine2 || '',
        city,
        state,
        zipCode,
        beneIdentification:
          currentIdentification || // istanbul ignore next // low value
          '',
        maskedSsn:
          currentSsnOrTaxId || // istanbul ignore next // low value
          '',
        maskedTaxId: '',
        identificationNumber:
          currentIdentificationNumber || // istanbul ignore next // low value
          '',
        issuer:
          taxIdentifier && taxIdentifier.length ? taxIdentifier[0].issuer : '',
        issueDate:
          taxIdentifier && taxIdentifier.length
            ? dayjs(
                convertDateToSafeFormat(taxIdentifier[0].issuedDate),
              ).format('MM/DD/YYYY')
            : '',
        expirationDate:
          taxIdentifier && taxIdentifier.length
            ? dayjs(
                convertDateToSafeFormat(taxIdentifier[0].expiryDate),
              ).format('MM/DD/YYYY')
            : '',
        fundAllocationType:
          allocationPercentage.toLocaleLowerCase() === 'equal'
            ? 'equal'
            : 'specific',
      }}
      onSubmit={handleSubmit}
    >
      {isVerifyPage ? (
        <EditBeneficiaryVerifyPage
          accountId={accountId}
          beneficiaryId={beneficiaryId}
          beneficiaries={beneficiaries}
          stepBack={stepBack}
        />
      ) : (
        <EditBeneficiaryForm
          beneficiaryId={beneficiaryId}
          beneficiaries={beneficiaries}
          isLoading={isLoading}
          showErrorMessage={showErrorMessage}
          isIraPage={isIraPage}
          stepForward={stepForward}
        />
      )}
    </Forms>
  )
}

export const EditBeneficiaryModal: React.FC<{
  beneficiaryId: string
  beneficiaries: Beneficiary[] | undefined
  accountId: string
  accountLabel: string
  isIraPage: boolean
}> = ({ beneficiaryId, beneficiaries, accountId, accountLabel, isIraPage }) => {
  return (
    <Modal
      trigger={
        <EditButton
          isIraPage={isIraPage}
          beneficiaryId={beneficiaryId}
          accountId={accountId}
          accountLabel={accountLabel}
        />
      }
      content={
        <EditModalContent
          beneficiaryId={beneficiaryId}
          beneficiaries={beneficiaries}
          accountId={accountId}
          isIraPage={isIraPage}
        />
      }
      fullScreenMd
      width="md"
      disableCloseOnOverlayClick
    />
  )
}
